import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Linkedin, Instagram, Youtube } from 'lucide-react';
import axios from 'axios';
import { siteConfig } from '@/config/siteConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const [qrCode, setQrCode] = useState(null);

  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        const response = await axios.get(`${API}/qr-code`);
        setQrCode(response.data.qr_code_base64);
      } catch (error) {
        console.error('Failed to fetch QR code:', error);
      }
    };
    fetchQRCode();
  }, []);

  return (
    <footer className="bg-[#1e293b] text-white mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-[#d4af37]">{siteConfig.agentName}</h3>
            <p className="text-slate-300 text-sm mb-4">
              {siteConfig.tagline} serving California and Florida. Committed to helping you find your dream home.
            </p>
            <div className="space-y-2 text-sm text-slate-300">
              <p>{siteConfig.californiaLicense}</p>
              <p>{siteConfig.floridaLicense}</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="text-slate-300 hover:text-[#d4af37] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-slate-300 hover:text-[#d4af37] transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-300 hover:text-[#d4af37] transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-slate-300 hover:text-[#d4af37] transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/signup" className="text-slate-300 hover:text-[#d4af37] transition-colors">
                  Get Started
                </Link>
              </li>
              <li>
                <Link to="/leave-review" className="text-slate-300 hover:text-[#d4af37] transition-colors">
                  Leave a Review
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-2 text-slate-300">
                <Phone className="h-4 w-4" />
                <span>{siteConfig.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-300">
                <Mail className="h-4 w-4" />
                <span>{siteConfig.email}</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-300">
                <MapPin className="h-4 w-4" />
                <span>{siteConfig.locations}</span>
              </div>
            </div>
            <div className="flex space-x-4 mt-4">
              <a
                href={siteConfig.socialMedia.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-[#d4af37] transition-colors"
                data-testid="linkedin-link"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href={siteConfig.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-[#d4af37] transition-colors"
                data-testid="instagram-link"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={siteConfig.socialMedia.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-[#d4af37] transition-colors"
                data-testid="youtube-link"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* QR Code */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Scan to Get Started</h3>
            <div className="bg-white p-3 rounded-lg inline-block">
              {qrCode ? (
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="Signup QR Code"
                  className="w-32 h-32"
                  data-testid="footer-qr-code"
                />
              ) : (
                <div className="w-32 h-32 bg-slate-200 animate-pulse rounded" />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">Scan to visit our signup page</p>
          </div>
        </div>

        <div className="border-t border-slate-700 mt-8 pt-8 text-center text-sm text-slate-400">
          <p>&copy; {siteConfig.copyrightYear} {siteConfig.companyName}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
